<template lang="pug">
  div
    .fixed.w-full.v-full.bg-gray-900.top-0.right-0.p-5.flex.justify-start.header(class="md:pl-10")
      .pt-1(class="sm:ml-6 md:ml-48")
        a(@click="logPdEvent('Header', 'User clicked Pipedream logo')" href="https://pipedream.com" target="_blank")
          PipedreamLogo(height="20")
      .ml-auto(class="md:w-1/8 sm:flex-wrap")
        a.text-sm.font-bold(@click="logPdEvent('Header', 'User clicked Docs link')" href="https://github.com/PipedreamHQ/pipedream" target="_blank") Github
      .ml-6(class="md:w-1/8 sm:flex-wrap")
        a.text-sm.font-bold(@click="logPdEvent('Header', 'User clicked Docs link')" href="https://docs.pipedream.com/" target="_blank") Docs
      .ml-6(class="sm:mr-6 md:mr-48 md:w-1/8 sm:flex-wrap")
        a.text-sm.font-bold(@click="logPdEvent('Header', 'User clicked Community link')" href="https://pipedream.com/community" target="_blank") Community
    .bg-grey-lighter.max-w-5xl.mx-auto.mt-24(class="md:mt-24")
      .ml-10.mr-10
        RSS.mx-auto.mt-8.mb-8(height="80px")
        .max-w-4xl.mx-auto.mb-6
          h2.mb-3.text-5xl.font-extrabold.title(class="md:text-6xl") RSS for Hackers
        .max-w-2xl.mx-auto.mb-3
          h3.text-xl.mb-16 Patterns for processing and producing RSS feeds
      .max-w-5xl.mx-auto.text-left.ml-10.mr-10
        .flex.flex-wrap.justify-around.justify-center.mt-10.m-1(class="md:flex-no-wrap")
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/3") 
            a(href="#getting-started" @click="logPdEvent('Sections', 'User clicked Getting Started Section')")
              .w-full.h-full.p-6
                .text-center.mx-auto.mb-5
                  RSSIcon.mr-3.inline(width="40px" height="40px" fill="#ffffff")
                h3.text-xl.font-semibold.text-center.mb-4 Getting Started
                p.text-lg.mb-4.text-center Create an event source from an RSS feed so you can run code on new items
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/3") 
            a(href="#example-workflows" @click="logPdEvent('Sections', 'User clicked Example Workflows Section')")
              .w-full.h-full.p-6
                .text-center.mx-auto.mb-5
                  PipedreamIcon.mr-3.inline(width="40px" height="40px")
                h3.text-xl.font-semibold.text-center.mb-4 Example Workflows
                p.text-lg.mb-4.text-center Send an RSS feed to Discord or SQS, trigger a Lambda, run Puppeteer script on every new item, and more
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/3") 
            a(href="#apis" @click="logPdEvent('Sections', 'User clicked APIs section')")
              .w-full.h-full.p-6
                .text-center.mx-auto.mb-5
                  Terminal.mr-3.inline(width="40px" height="40px" fill="#ffffff")
                h3.text-xl.font-semibold.text-center.mb-4 SSE + REST API
                p.text-lg.mb-4.text-center Process events in real time using a private SSE stream, or in batch using a REST API
      p.mb-24(id="getting-started")
      .max-w-5xl.mx-auto.text-left.ml-10.mr-10.text-xl
        p.text-green.text-3xl.font-bold.mb-3 Getting Started
        p.mb-6 If you want to process an RSS feed programmatically, you have to run code to poll the feed and keep track of items already processed. This isn't hard to write, but it's often not core to your app's logic. 
          span.font-bold You probably just want to run code on each new item in the feed.
        .code.text-left.mb-6
          p // Don't care how I get feed
          p for (const item of feed) {
          p.ml-8 // Just want to run code
          p }
        p.mb-6 Pipedream lets you treat an RSS feed as an
          a.font-bold.text-blue-500.ml-1(href="https://docs.pipedream.com/event-sources/" target="_blank") event source
          | . Pipedream runs the code to poll the feed, emitting new items as the feed produces them:
        .mx-auto.text-center.mb-6.event-source-flow-desktop
          RSS.mx-auto.mt-8.mb-8.inline.mr-8(height="40px" width="40px")
          ArrowLeft.inline.white.mr-8(width="25px" height="25px")
          PipedreamLogo.inline.mr-8(height="20")
          ArrowRight.inline.white.mr-4(width="25px" height="25px")
          img.inline.mr-8(src="@img/example-rss-payload.png" width="400px" alt="Example RSS payload")
        .mx-auto.text-center.mb-6.event-source-flow-mobile
          RSS.mx-auto.mt-4(height="40px" width="40px")
          ArrowUp.mx-auto.white.mt-4(width="25px" height="25px")
          PipedreamLogo.mx-auto.mt-4(height="20")
          ArrowDown.mx-auto.mt-4(width="25px" height="25px")
          img.mx-auto.mt-4(src="@img/example-rss-payload.png" width="400px" alt="Example RSS payload")
        p.mb-6 Then, you can process items from your event source in 3 ways:
        ul.list-disc.mb-6
          li.ml-8.mt-4 Subscribe to a private SSE stream, which publishes items in real time
          li.ml-8 Trigger a Pipedream workflow for each item, running hosted Node.js code (also real time)
          li.ml-8 Access items in batch using Pipedream's REST API
        p.mb-6 To get started,
          a.text-blue-500.ml-1(href="https://pipedream.com/sources?action=create&url=https%3A%2F%2Fgithub.com%2FPipedreamHQ%2Fpipedream%2Fblob%2Fmaster%2Fcomponents%2Frss%2Frss.js&app=none" target="_blank" @click="logPdEvent('CTA', 'User clicked create a new RSS source link')") create a new RSS source in the Pipedream UI
          | . Name the source and add your feed URL:
        .mx-auto.max-w-3xl
          img.mb-6(src="@img/rss-source-in-ui.png"  alt="RSS Source in Pipedream UI")
        p.mb-6 You can also visit
          a.text-blue-500.ml-1.mr-1.break-words(href="https://pipedream.com/sources" target="_blank") https://pipedream.com/sources
          | and click the
          span.font-bold.ml-1.mr-1 Create Source
          | button, then choose the 
          span.font-mono.ml-1.mr-1 rss/rss.js
          | source from the modal that appears.
        p.mb-6 If you don't have a feed handy, you can use 
          a.text-blue-500.ml-1.break-words(href="https://rss.m.pipedream.net" target="_blank") https://rss.m.pipedream.net
          | . This generates a new, random item once every 15 minutes.
        p.mb-6 You can also create an event source using the Pipedream CLI:
        .code.text-left.px-10.mb-6.break-words
          p $ curl https://cli.pipedream.com/install | sh
          p $ pd deploy 
            span.ml-6 # Select the rss source, enter your URL
        p.mb-6 Once deployed, you can retrieve items emitted by the source using the 
          span.font-mono.ml-2.mr-2 pd events
          | command:
        .code.text-left.px-10.mb-6
          p $ pd events -n 1 sample-feed
            span.ml-6 # Retrieve the newest item
          p $ pd events -f sample-feed
            span.ml-6 # Tail the feed in real time
        p.mb-6 Then, you can trigger a
          a.text-blue-500.ml-1.mr-1(href="https://pipedream.com/new" target="_blank") Pipedream workflow
          | &mdash; a serverless Node.js script &mdash; on every event:
          .max-w-2xl.mx-auto
            img.mt-6.mb-6(src="@img/new-workflow-source.png" alt="New workflow source")
        p.mb-6 or process items in your own app using
          a.text-blue-500.ml-1.mr-1(href="#apis") the SSE stream or REST API
          | tied to your source.
      p.mb-24(id="example-workflows")
      .max-w-5xl.mx-auto.text-left.ml-10.mr-10.text-xl
        p.text-green.text-3xl.font-bold.mb-3 Example Workflows
        h3.mb-6 Workflows are linear sequences of steps &mdash; just Node.js code &mdash;
          span.ml-1 hosted and run by Pipedream.
        h3.mb-6 
          span.font-bold You can copy the workflows below and run them in your Pipedream account
          a.text-blue-500.font-bold.ml-1(href="https://docs.pipedream.com/pricing/" target="_blank") for free
          | . When you copy these workflows, you'll be asked to add the URL to your RSS feed, and to name the feed something memorable. This creates an
          a.text-blue-500.mr-1.ml-1(href="#getting-started") RSS event source
          | and runs your workflow every time an item is added to your feed. 
        h3.mb-6 If you already created RSS source, and want to run a workflow on that same feed, you can click the
          span.font-bold.mr-1.ml-1 Select Another Trigger
          | button and choose from any event source already running in your account.
        .max-w-2xl.mx-auto.mb-6
          img(src="@img/select-another-trigger.png" alt="Select Another Trigger button")
        h3 They should work as is, but you can modify them in any way. For example, you can add a step to
          a.ml-1.text-blue-500(href="https://docs.pipedream.com/workflows/steps/code/" target="_blank") run any Node.js code 
          | or use 
          a.text-blue-500(href="https://docs.pipedream.com/workflows/steps/actions/" target="_blank") pre-built functions 
          | to send data to other destinations.
        .flex.flex-wrap.justify-around.justify-center.mt-10.m-1(class="md:flex-no-wrap")
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-to-email-p_NMCqyV/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to Email')")
              .w-full.h-full.p-10
                .text-center.mx-auto.mb-5
                  RSS.mr-3.inline(width="40px" height="40px")
                  ArrowRight.mr-3.inline.white(width="25px" height="25px")
                  Mail.inline(height="40px" width="40px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to Email
                p.text-lg.mb-4.text-center Send you a formatted email with new items in the feed
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylan/rss-to-twitter-p_5VCkQ7/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to Twitter')")
              .w-full.h-full.p-10
                .text-center.mx-auto.-mt-4.mb-2
                  RSS.mr-3.inline(width="40px" height="40px")
                  ArrowRight.inline.white(width="25px" height="25px")
                  Twitter.inline(height="70px" width="70px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to Twitter
                p.text-lg.mb-4.text-center Posts a tweet for every new item in an RSS feed
        .flex.flex-wrap.justify-around.justify-center.m-1(class="md:flex-no-wrap")
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-aws-sqs-p_D1CDjB/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to SQS')")
              .w-full.h-full.p-10
                .text-center.mx-auto.mb-5
                  RSS.mr-3.inline(width="40px" height="40px")
                  ArrowRight.mr-3.inline.white(width="25px" height="25px")
                  SQS.inline(height="40px" width="40px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to AWS SQS
                p.text-lg.mb-4.text-center Send new items to an SQS queue
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-aws-eventbridge-event-bus-p_JZCk29/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to EventBridge')")
              .w-full.h-full.p-10
                .text-center.mx-auto.mb-5
                  RSS.mr-3.inline(width="40px" height="40px")
                  ArrowRight.mr-3.inline.white(width="25px" height="25px")
                  EventBridge.inline(height="40px" width="40px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to AWS EventBridge
                p.text-lg.mb-4.text-center Send new items to an event bus
        .flex.flex-wrap.justify-around.justify-center.m-1(class="md:flex-no-wrap")
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-aws-lambda-p_ZJC9BG/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to Lambda')")
              .w-full.h-full.p-10
                .text-center.mx-auto.mb-5
                  RSS.mr-3.inline(width="40px" height="40px")
                  ArrowRight.mr-3.inline.white(width="25px" height="25px")
                  Lambda.inline(height="40px" width="40px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to AWS Lambda
                p.text-lg.mb-4.text-center Invoke a Lambda function for every new item using the AWS SDK
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-http-request-p_MOCq8K/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to Webhook')")
              .w-full.h-full.p-10
                .text-center.mx-auto.mb-5
                  RSS.mr-3.inline(width="40px" height="40px")
                  ArrowRight.mr-3.inline.white(width="25px" height="25px")
                  Webhook.inline(height="40px" width="40px" fill="#ffffff")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to Webhook
                p.text-lg.mb-4.text-center Send an HTTP POST request with the item in the payload, to a URL you specify
        .flex.flex-wrap.justify-around.justify-center.m-1(class="md:flex-no-wrap")
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-slack-p_YyCDyK/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to Slack')")
              .w-full.h-full.py-6.px-10
                .text-center.mx-auto.mb-2
                  RSS.ml-6.mr-3.inline(width="40px" height="40px")
                  ArrowRight.inline.white(width="25px" height="25px")
                  Slack.-ml-2.inline(height="80px" width="80px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to Slack
                p.text-lg.mb-4.text-center Sends a formatted message to Slack
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-discord-p_7NCWrm/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to Discord')")
              .w-full.h-full.p-10
                .text-center.mx-auto.mb-5
                  RSS.mr-3.inline(width="40px" height="40px")
                  ArrowRight.mr-3.inline.white(width="25px" height="25px")
                  Discord.inline(height="50px" width="50px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to Discord
                p.text-lg.mb-4.text-center Sends a formatted message to Discord
        .flex.flex-wrap.justify-around.justify-center.m-1(class="md:flex-no-wrap")
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-telegram-p_PACwrm/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to Telegram')")
              .w-full.h-full.py-6.px-10
                .text-center.mx-auto.mb-6.mt-5
                  RSS.mr-3.inline(width="40px" height="40px")
                  ArrowRight.mr-3.inline.white(width="25px" height="25px")
                  Telegram.inline(height="40px" width="40px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to Telegram
                p.text-lg.mb-4.text-center Sends a formatted message to Telegram
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-to-google-sheets-p_ezCqGG/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to Google Sheets')")
              .w-full.h-full.p-10
                .text-center.mx-auto.mb-6.mt-1
                  RSS.ml-3.mr-3.inline(width="40px" height="40px")
                  ArrowRight.inline.white.mr-3(width="25px" height="25px")
                  img.inline(src="@img/google-sheets.png" alt="Google Sheets icon" height="40px" width="40px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to Google Sheets
                p.text-lg.mb-4.text-center Add a row in a Google sheet for every new item
        .flex.flex-wrap.justify-around.justify-center.m-1(class="md:flex-no-wrap")
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/generate-an-rss-feed-from-http-post-requests-retrieve-via-get-request-p_n1CrQG/readme" target="_blank" @click="logPdEvent('Workflows', 'HTTP to RSS')")
              .w-full.h-full.p-10
                .text-center.mx-auto.mb-6
                  Browser.mr-3.inline(height="40px" width="40px")
                  ArrowRight.mr-3.inline.white(width="25px" height="25px")
                  RSS.inline(width="40px" height="40px")
                h3.text-xl.font-semibold.text-center.mb-4 HTTP to RSS
                p.text-lg.mb-4.text-center Generate an RSS feed from HTTP POST requests, retrieve via GET request
          .max-w-md.rounded.m-1.bg-gray-800(class="md:w-1/2 sm:w-full") 
            a(href="https://pipedream.com/@dylburger/rss-to-browserless-to-s3-p_95Cv5z/readme" target="_blank" @click="logPdEvent('Workflows', 'RSS to Browserless to S3')")
              .w-full.h-full.py-6.px-10
                .text-center.mx-auto.mb-6.mt-4
                  RSS.inline(width="40px" height="40px" class="md:mr-3 sm:mr-2")
                  ArrowRight.inline.white(width="25px" height="25px" class="md:mr-3 sm:mr-2")
                  // vue-loader failed to load this SVG correctly, so we include it inline
                  svg.inline(class="md:mr-3 sm:mr-2" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" style="isolation:isolate" viewBox="1123.044 453.491 93.911 147.019" width="40px" height="40px")
                    path(d=" M 1180.143 521.337 L 1193.478 527.466 L 1207.698 534.002 C 1212.807 536.35 1216.956 542.822 1216.956 548.445 L 1216.956 559.839 L 1216.956 571.233 C 1216.956 576.856 1212.807 583.328 1207.698 585.676 L 1193.478 592.212 L 1179.257 598.748 C 1174.148 601.097 1165.852 601.097 1160.742 598.748 L 1146.522 592.212 L 1132.302 585.676 C 1127.193 583.328 1123.044 576.856 1123.044 571.233 L 1123.044 559.839 L 1123.044 548.445 C 1123.044 547.762 1123.106 547.067 1123.222 546.367 L 1123.222 468.669 C 1123.222 463.978 1126.844 458.993 1131.305 457.544 L 1142.555 453.889 C 1147.016 452.439 1150.637 455.071 1150.637 459.761 L 1150.637 525.574 L 1150.659 525.564 C 1150.644 525.577 1150.636 525.584 1150.637 525.585 L 1166.521 543.39 L 1141.468 543.944 C 1141.093 543.931 1140.817 543.934 1140.632 543.956 C 1139.762 544.057 1139.006 544.357 1138.368 544.854 C 1137.607 545.508 1137.519 546.14 1138.1 546.751 L 1178.18 589.359 C 1178.858 589.988 1179.859 590.225 1181.188 590.071 C 1181.371 590.05 1181.689 589.985 1182.141 589.876 C 1182.903 589.647 1183.457 589.314 1183.802 588.878 C 1184.146 588.443 1184.201 588.013 1183.966 587.587 L 1168.776 557.871 L 1194.905 556.344 C 1195.092 556.35 1195.368 556.332 1195.735 556.289 C 1196.56 556.194 1197.252 555.959 1197.812 555.583 C 1198.588 555.068 1198.823 554.49 1198.516 553.847 L 1180.143 521.337 Z " fill-rule="evenodd" fill="#fefefe")
                  ArrowRight.inline.white(width="25px" height="25px" class="md:mr-3 sm:mr-2")
                  S3.inline(width="40px" height="40px")
                h3.text-xl.font-semibold.text-center.mb-4 RSS to Browserless to S3
                p.text-lg.mb-4.text-center Take a screenshot of every new URL, save to Amazon S3
      p.mb-24(id="apis")
      .max-w-5xl.mx-auto.text-left.ml-10.mr-10.text-xl
        p.text-green.text-3xl.font-bold.mb-6 SSE and REST APIs
        p.mb-6 Pipedream provides two other interfaces for accessing events produced by sources: 
        ul.list-disc.mb-6
          li.ml-8.mt-4 A private SSE stream specific to your source
          li.ml-8 A REST API
        p.mb-12 This way, you can run an event source in Pipedream but access its events in your own application.
        p.font-bold.mb-6.text-2xl(id="sse-interface") SSE
        p.mb-6 SSE &mdash;
          a.text-blue-500.ml-1.mr-1(href="https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events" target="_blank") Server-sent events
          | &mdash;
          span.ml-1 defines a spec for how servers can send events directly to clients that subscribe to those events, similar to
          a.text-blue-500.ml-1(href="https://developer.mozilla.org/en-US/docs/Web/API/WebSockets_API" target="_blank") WebSockets 
          | and related server-to-client push technologies. If you listen for new items using SSE, 
          span.font-bold you can run any code in your own application, in real time, as soon as those items are added to the feed.
        p.mb-6 Streams are private by default. You authenticate by passing your Pipedream API key in the Authorization header:
        .code.text-left.px-10.mb-6.break-words
          p $ curl -H "Authorization: Bearer API_KEY" \
          p.ml-10 https://api.pipedream.com/sources/SOURCE_ID/sse
        p.mb-12
          a.text-blue-500.mr-1(href="https://docs.pipedream.com/api/sse" target="_blank") See the Pipedream SSE docs 
          | for more information on authentication, example Node code, and other details on the interface.
        p.font-bold.mb-6.text-2xl(id="rest-api") REST API
        p.mb-6 If you prefer to process items from an RSS feed in batch, you can fetch them using 
          a.text-blue-500.ml-1(href="https://docs.pipedream.com/api/rest/" target="_blank") Pipedream's REST API
          | :
        .code.text-left.px-10.mb-6.break-words
          p $ curl -H "Authorization: Bearer API_KEY" \
          p.ml-10 https://api.pipedream.com/v1/sources/SOURCE_ID/events?n=1
        p.mb-6 Note the
          span.font-mono.ml-1.mr-1 ?n=1
          | query string. You can vary the number of events returned (most recent first) by setting this param.
      .max-w-5xl.mx-auto.text-left.ml-10.mr-10.text-xl.mt-24
        p.text-green.font-bold.mb-3.text-3xl Questions or Feedback?
        p.mb-6 Please
          a.text-blue-500.ml-1.mr-1(href="https://docs.pipedream.com/support/" target="_blank") reach out
          | with any questions or feedback. We're happy to add other RSS-specific developer resources to this list, and we'd love to hear what can be improved about event sources or the example workflows.
        .mt-24.mb-24
          hr.border-gray-500.border
          .pt-12.flex.flex-wrap.justify-start
            .flex-start(class="md:w-1/3 sm:flex-wrap")
              p.text-sm.text-gray-500 Pipedream, Inc. &mdash; San Francisco, CA
            .ml-auto(class="md:w-1/8 sm:flex-wrap")
              a.text-sm.text-blue-500(@click="logPdEvent('Footer', 'User Clicked Twitter link')" href="https://twitter.com/pipedream" target="_blank") Twitter
            .ml-6(class="md:w-1/8 sm:flex-wrap")
              a.text-sm.text-blue-500(@click="logPdEvent('Footer', 'User Clicked Community link')" href="https://pipedream.com/community" target="_blank") Community
            .ml-6(class="md:w-1/8 sm:flex-wrap")
              a.text-sm.text-blue-500(@click="logPdEvent('Footer', 'User Clicked Docs link')" href="https://docs.pipedream.com/" target="_blank") Docs
            .ml-6(class="md:w-1/8 sm:flex-wrap")
              a.text-sm.text-blue-500(@click="logPdEvent('Footer', 'User Clicked Terms link')" href="https://pipedream.com/terms" target="_blank") Terms
            .ml-6(class="md:w-1/8 sm:flex-wrap")
              a.text-sm.text-blue-500(@click="logPdEvent('Footer', 'User Clicked Privacy link')" href="https://pipedream.com/privacy" target="_blank") Privacy
</template>

<script>
import ArrowDown from "@icons/arrow-down.svg?inline";
import ArrowLeft from "@icons/arrow-left.svg?inline";
import ArrowRight from "@icons/arrow-right.svg?inline";
import ArrowUp from "@icons/arrow-up.svg?inline";
import Bell from "@icons/bell.svg?inline";
import Browser from "@icons/browser.svg?inline";
import Clock from "@icons/clock.svg?inline";
import Code from "@icons/code.svg?inline";
import DeviceMobile from "@icons/device-mobile.svg?inline";
import Heart from "@icons/heart.svg?inline";
import Mail from "@icons/mail.svg?inline";
import PipedreamIcon from "@icons/pipedream.svg?inline";
import RSSIcon from "@icons/rss.svg?inline";
import Server from "@icons/server.svg?inline";
import Terminal from "@icons/terminal.svg?inline";

import Browserless from "@logos/browserless.svg";
import Discord from "@logos/discord.svg?inline";
import EventBridge from "@logos/eventbridge.svg?inline";
import Lambda from "@logos/lambda.svg?inline";
import PipedreamLogo from "@logos/pipedream.svg?inline";
import RSS from "@logos/rss.svg?inline";
import S3 from "@logos/s3.svg?inline";
import Slack from "@logos/slack.svg?inline";
import SQS from "@logos/sqs.svg?inline";
import Telegram from "@logos/telegram.svg?inline";
import Twitter from "@logos/twitter.svg?inline";
import Webhook from "@logos/webhook.svg?inline";

import { logPipedreamEvent } from "@plugins/analytics";

export default {
  name: "Main",
  props: {
    title: String
  },
  components: {
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    Bell,
    Browser,
    Browserless,
    Clock,
    Code,
    DeviceMobile,
    Discord,
    EventBridge,
    Heart,
    Lambda,
    Mail,
    PipedreamIcon,
    PipedreamLogo,
    RSS,
    RSSIcon,
    S3,
    Server,
    Slack,
    SQS,
    Telegram,
    Terminal,
    Twitter,
    Webhook
  },
  created: function() {
    logPipedreamEvent({
      category: "INIT",
      action: "Page Load"
    });
  },
  methods: {
    logPdEvent: function(category, action) {
      logPipedreamEvent({
        category,
        action
      });
    }
  }
};
</script>

<style scoped>
.pd-green {
  background-color: #34d28b;
}
.text-green {
  color: #34d28b;
}

/* SVG classes */

.st0 {
  fill: #e01e5a;
}
.st1 {
  fill: #36c5f0;
}
.st2 {
  fill: #2eb67d;
}
.st3 {
  fill: #ecb22e;
}
.cls-1 {
  fill: #ff4f8b;
}
.cls-2 {
  fill: #232f3e;
}

.lambda-cls-1 {
  fill: #f90;
}

.twitter-cls-1 {
  fill: none;
}

.twitter-cls-2 {
  fill: #1da1f2;
}

.s3-cls-1 {
  fill: #6cae3e;
}

.button {
  stroke: none;
  fill: orange;
}
.symbol {
  stroke: none;
  fill: white;
}

.discord-st0 {
  fill: #ffffff;
}
.rollbar-st0 {
  fill: #ffffff;
}

@media screen and (max-width: 1200px) {
  .event-source-flow-desktop {
    visibility: hidden;
    height: 0;
    padding: 0;
    margin-bottom: 1rem;
  }
}

@media screen and (min-width: 1200px) {
  .event-source-flow-mobile {
    visibility: hidden;
    height: 0;
    padding: 0;
    margin-bottom: 1rem;
  }
}

.header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(34, 34, 34, 0.85);
}

.code {
  @apply font-mono py-10 px-10 rounded;
  background-color: #000000;
}

@media screen and (max-width: 640px) {
  .code {
    @apply text-base px-3 py-5;
  }
}

.title {
  line-height: 1.162;
}
.introducing-title {
  letter-spacing: 0.85vw;
}
</style>
